import { AriaOrb } from "../AriaOrb";
import { X, Mic, Loader2, Volume2 } from "lucide-react";

export const STATE = {
  IDLE: "idle", LISTENING: "listening", RECORDING: "recording",
  TRANSCRIBING: "transcribing", THINKING: "thinking", SPEAKING: "speaking", ERROR: "error",
};

const STATUS_COPY = {
  idle: "Tap to start the conversation",
  listening: "I'm listening — speak naturally",
  recording: "I hear you…",
  transcribing: "Catching that…",
  thinking: "Looking through your lectures, notes and papers…",
  speaking: "ARIA is speaking",
  error: "Something went wrong — tap to retry",
};
const ORB_STATE = { listening: "listening", recording: "listening", speaking: "listening", thinking: "thinking", transcribing: "thinking" };
const GLOW_OPACITY = { recording: 0.55, speaking: 0.40 };
const STATUS_ICON = { thinking: Loader2, speaking: Volume2, listening: Mic };

function Backdrop({ state }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <img src="https://static.prod-images.emergentagent.com/jobs/b0811d92-88d8-4d42-b7df-e17c59ab6088/images/bcff08a53682e7094e25acfbbe7bcebc1abc4ec3ef03cd34f2f7cbc6af1663f3.png"
        alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 dark:opacity-40"
        style={{ filter: "blur(48px) saturate(140%)" }} onError={(e) => { e.target.style.display = "none"; }} />
      <div className="conv-aurora" />
      <div className="absolute inset-0 transition-opacity duration-700"
        style={{ opacity: GLOW_OPACITY[state] ?? 0.18, background: "radial-gradient(circle at 50% 55%, rgba(37,99,255,0.35) 0%, transparent 55%)" }} />
      <div className="conv-bands" style={{ top: "30%" }} />
      <div className="conv-bands" style={{ top: "70%" }} />
      <div className="absolute inset-0 bg-[var(--aria-bg)]/55" />
    </div>
  );
}

function InputMeter({ waveform }) {
  return (
    <div className="relative z-10 px-8 py-5 border-t border-[var(--aria-border)]/40 backdrop-blur-md">
      <div className="max-w-xl mx-auto flex items-center gap-3">
        <span className="text-[9px] uppercase tracking-[0.28em] font-mono text-[var(--aria-text-muted)]">INPUT</span>
        <div className="flex-1 h-[3px] bg-[var(--aria-surface-2)] overflow-hidden">
          <div className="h-full transition-all duration-75"
            style={{ width: `${Math.min(100, waveform * 100)}%`, background: "var(--aria-grad-brand)", boxShadow: "0 0 12px var(--aria-primary)" }} />
        </div>
        <span className="text-[10px] font-mono text-[var(--aria-text-muted)] tabular-nums w-10 text-right">
          {String(Math.round(waveform * 100)).padStart(2, "0")}%
        </span>
      </div>
    </div>
  );
}

export function ConversationStage({ state, transcript, reply, waveform, accent, nickname, wakeWordRequired, onClose }) {
  const Icon = STATUS_ICON[state];
  const orbScale = state === STATE.RECORDING ? 1 + waveform * 0.25 : 1;
  const hint = wakeWordRequired
    ? `try:  "Hey ${nickname}, why is ΔG zero at equilibrium?"`
    : `try:  "Quiz me on chemical potential."`;
  return (
    <div className="fixed inset-0 z-50 bg-[var(--aria-bg)] flex flex-col" data-testid="conversation-mode-overlay">
      <Backdrop state={state} />
      <div className="relative z-10 flex items-center justify-between px-8 py-5 border-b border-[var(--aria-border)]/40 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <span className="az-chevron" />
          <div className="w-1.5 h-1.5 rounded-full bg-[var(--aria-primary)] animate-pulse shadow-[0_0_12px_var(--aria-primary)]" />
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--aria-text-muted)]">
            Conversation mode {wakeWordRequired ? "·  wake-word: Hey ARIA" : "·  always listening"}
          </div>
        </div>
        <button onClick={onClose} data-testid="conversation-close"
          className="p-2 rounded-sm border border-transparent hover:border-[var(--aria-border)] hover:bg-[var(--aria-surface-2)] transition-colors">
          <X size={18} />
        </button>
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
        <div className="transition-transform duration-100 ease-out" style={{ transform: `scale(${orbScale})` }}>
          <AriaOrb size={240} state={ORB_STATE[state] || "idle"} color={accent} />
        </div>
        <div className="mt-12 text-center max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.32em] font-mono text-[var(--aria-text-muted)] mb-5">
            {Icon && <Icon size={11} className={`inline -mt-0.5 mr-1.5 ${state === STATE.THINKING ? "animate-spin" : ""}`} />}
            {STATUS_COPY[state]}
          </div>
          {transcript && (
            <div className="text-2xl leading-relaxed text-[var(--aria-text-muted)] italic mb-5 font-display" data-testid="conversation-transcript">"{transcript}"</div>
          )}
          {reply && (
            <div className="text-4xl leading-[1.25] tracking-tight text-[var(--aria-text)] font-display font-medium" data-testid="conversation-reply">
              {reply}{state === STATE.THINKING && <span className="stream-cursor" aria-hidden />}
            </div>
          )}
          {!transcript && !reply && <div className="text-base text-[var(--aria-text-faint)] italic mt-2 font-mono">{hint}</div>}
        </div>
      </div>
      <InputMeter waveform={waveform} />
    </div>
  );
}
